import React, { useState } from 'react';
import { LuArrowLeft, LuArrowRight, LuSearch } from 'react-icons/lu';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { docsSections } from './shared';

const DocsLayout: React.FC = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();

  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const visibleSections = terms.length
    ? docsSections.filter((s) => {
        const haystack = `${s.label} ${s.keywords ?? ''}`.toLowerCase();
        return terms.every((t) => haystack.includes(t));
      })
    : docsSections;

  const currentIndex = docsSections.findIndex((s) => {
    const to = s.path ? `/docs/${s.path}` : '/docs';
    return location.pathname === to;
  });
  const prev = currentIndex > 0 ? docsSections[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < docsSections.length - 1 ? docsSections[currentIndex + 1] : null;
  const toPath = (s: { path: string }) => (s.path ? `/docs/${s.path}` : '/docs');

  return (
    <div className="min-h-screen bg-base-100 text-base-content">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-base-300 bg-base-100/90 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="MFlow" className="h-7 w-7" />
            <span className="font-display text-lg font-bold">MFlow</span>
            <span className="text-base-content/40 text-sm hidden sm:inline">/ Docs</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth" className="btn btn-sm btn-primary">
              Sign In
            </Link>
            <button className="btn btn-sm btn-ghost lg:hidden" onClick={() => setMobileNavOpen((v) => !v)}>
              ☰
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-30 w-64 bg-base-100 border-r border-base-300 pt-16 pb-8 overflow-y-auto
            transform transition-transform duration-200
            lg:static lg:translate-x-0 lg:pt-8 lg:h-[calc(100vh-3.5rem)] lg:sticky lg:top-14
            ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <nav className="px-4 space-y-0.5">
            <label className="input input-bordered input-sm flex items-center gap-2 mb-4">
              <LuSearch className="opacity-50 shrink-0" size={14} />
              <input
                type="search"
                className="grow min-w-0"
                placeholder="Search docs"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <div className="text-xs font-semibold uppercase tracking-widest text-base-content/40 mb-3 px-2">
              Contents
            </div>
            {visibleSections.length === 0 && (
              <div className="px-2 py-2 text-sm text-base-content/50">No sections match &quot;{query}&quot;.</div>
            )}
            {visibleSections.map((s) => (
              <NavLink
                key={s.path}
                to={s.path ? `/docs/${s.path}` : '/docs'}
                end
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  `block w-full text-left px-2 py-2 rounded text-sm transition-colors ${
                    isActive
                      ? 'bg-primary/15 text-primary font-medium'
                      : 'text-base-content/60 hover:text-base-content hover:bg-base-200'
                  }`
                }
              >
                {s.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Mobile overlay */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/50 lg:hidden"
            onClick={() => setMobileNavOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileNavOpen(false)}
            role="presentation"
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 px-6 py-10 lg:px-12 max-w-3xl">
          <Outlet />

          <div className="flex items-stretch justify-between gap-3 mt-12 pt-6 border-t border-base-300">
            {prev ? (
              <Link
                to={toPath(prev)}
                className="flex-1 flex items-center gap-2 rounded-lg bg-base-200 hover:bg-base-300 shadow-card px-4 py-3 text-sm transition"
              >
                <LuArrowLeft className="shrink-0 opacity-60" />
                <div className="min-w-0">
                  <div className="text-xs text-base-content/50">Previous</div>
                  <div className="font-medium truncate">{prev.label}</div>
                </div>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
            {next && (
              <Link
                to={toPath(next)}
                className="flex-1 flex items-center justify-end gap-2 rounded-lg bg-base-200 hover:bg-base-300 shadow-card px-4 py-3 text-sm text-right transition"
              >
                <div className="min-w-0">
                  <div className="text-xs text-base-content/50">Next</div>
                  <div className="font-medium truncate">{next.label}</div>
                </div>
                <LuArrowRight className="shrink-0 opacity-60" />
              </Link>
            )}
          </div>

          <div className="divider mt-8" />

          <footer className="text-xs text-base-content/40 pb-10 space-y-1">
            <p>© 2026 MFlow. All rights reserved.</p>
            <p>
              <a href="https://www.m-flow.io/terms-condition" className="link">
                Terms & Conditions
              </a>
              {' · '}
              <a href="https://www.m-flow.io/private-policy" className="link">
                Privacy Policy
              </a>
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default DocsLayout;
